import { collection, query, onSnapshot, addDoc, doc, updateDoc, runTransaction, serverTimestamp, deleteDoc } from "firebase/firestore";
import { db } from "../firebase-config";

// Snapshot listener for inventory orders
export const organizationInventoryOrdersSnapshotListener = (organization, onOrdersUpdate) => {
    const ordersQuery = query(collection(db, "organizations", organization, "inventory_orders"));
    const unsubscribe = onSnapshot(ordersQuery, (querySnapshot) => {
        const inventoryOrders = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        onOrdersUpdate(inventoryOrders);
    });
    return () => unsubscribe();
};

// Update an inventory order
export const updateInventoryOrder = async (organizationId, orderId, updatedOrder) => {
    try {
        const orderRef = doc(db, "organizations", organizationId, "inventory_orders", orderId);
        await updateDoc(orderRef, updatedOrder);
        return { success: true, error: null };
    } catch (error) {
        console.error("Error updating order:", error);
        return { success: false, error: error.message || 'Failed to update the order' };
    }
};

// Generate a unique order ID
const generateOrderId = (lastOrderNum) => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const sequence = String(lastOrderNum + 1).padStart(4, '0');
    return `${year}${month}${day}-${sequence}`;
};

// Add a new inventory order with transaction
export const addInventoryOrder = async (organization, orderData) => {
    try {
        const counterRef = doc(db, "organizations", organization, "counters", "orders");
        const newOrder = await runTransaction(db, async (transaction) => {
            const counterDoc = await transaction.get(counterRef);
            const currentCount = counterDoc.exists() ? counterDoc.data().count : 0;

            const orderId = generateOrderId(currentCount);
            transaction.set(counterRef, { count: currentCount + 1 }, { merge: true });

            const orderDocRef = doc(db, "organizations", organization, "inventory_orders", orderId);
            const enrichedOrderData = {
                ...orderData,
                order_id: orderId,
                created_at: serverTimestamp()
            };

            transaction.set(orderDocRef, enrichedOrderData);

            return { orderId, orderData: enrichedOrderData };
        });

        return { success: true, data: newOrder, error: null };
    } catch (error) {
        console.log(error);
        return { success: false, data: null, error: error.message || 'Error adding inventory order' };
    }
};

export const deleteInventoryOrder = async (organization, orderId) => {
    try {
        const orderRef = doc(db, "organizations", organization, "inventory_orders", orderId);
        await deleteDoc(orderRef);
        return { success: true, error: null };
    } catch (error) {
        console.error("Error deleting inventory order:", error);
        return { success: false, error: error.message || 'Failed to delete order' };
    }
};

// Real-time snapshot listener for a specific inventory order
export const inventoryOrderSnapshotListener = (organization, orderId, onOrderUpdate) => {
    const orderRef = doc(db, 'organizations', organization, 'inventory_orders', orderId);
    const unsubscribe = onSnapshot(orderRef, (docSnapshot) => {
        onOrderUpdate(docSnapshot.exists() ? { id: docSnapshot.id, ...docSnapshot.data() } : null);
    });
    return unsubscribe;
};

// Log order history
export const logOrderHistory = async (organizationId, orderId, changeType, oldValue, newValue, changedBy) => {
    try {
        const historyRef = collection(db, "organizations", organizationId, "inventory_orders", orderId, "history");
        await addDoc(historyRef, {
            change_type: changeType,
            old_value: oldValue,
            new_value: newValue,
            changed_by: changedBy,
            change_date: serverTimestamp()
        });
        return { success: true, error: null };
    } catch (error) {
        console.error("Error logging order history:", error);
        return { success: false, error: error.message || 'Failed to log order history' };
    }
};

// Real-time snapshot listener for order history
export const fetchOrderHistory = (organizationId, orderId, onHistoryUpdate) => {
    const historyQuery = query(collection(db, 'organizations', organizationId, 'inventory_orders', orderId, 'order_history'));
    const unsubscribe = onSnapshot(historyQuery, (snapshot) => {
        const history = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        onHistoryUpdate(history);
    });
    return unsubscribe;
};
